import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import "./styles.css";
import { Alert, Box, LinearProgress, Snackbar } from "@mui/material";
import { useTheme, ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import useMediaQuery from '@mui/material/useMediaQuery';
import Dashboard from "./pages/Dashboard/index";
import Account from "./pages/Account/index";
import IntegrationManagement from "./pages/IntegrationManagement/index"
import { useEffect, useState } from "react";
import AuthContext from "./context/AuthContext";
import { getToken } from "./services/Authentication";
import { getAccounts, getUsers } from "./services/Lookups";
import { useCookies } from "react-cookie";
import SnackbarContext from "./context/SnackbarContext";
import Login from "./components/Login"
import ScreenTooSmall from "./components/ScreenTooSmall"

function App() {
  const [cookies, setCookie] = useCookies();
  const [auth, setAuth] = useState(false);
  const [superUser, setSuperUser] = useState(false);
  const [userID, setUserID] = useState(0);
  const [username, setUsername] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userAccount, setUserAccount] = useState({});
  const [userAccounts, setUserAccounts] = useState([]);
  const [userAccountID, setUserAccountID] = useState(0);
  const [userAccountRole, setUserAccountRole] = useState("");
  const [token, setToken] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("");
  const [redirectToLogin, setRedirectToLogin] = useState(false);

  const handleRedirectToLogin = () => {
    setRedirectToLogin(true);
    console.log("Redirect to login", window.location.pathname)
    if (window.location.pathname !== "/") {window.location.pathname = "/"};
  };

  const setCookies = (token, user_id) => {
    setCookie('token', token, {domain: ".revoptimal.com", path:"/"});
    setCookie('user_id', user_id, {domain: ".revoptimal.com", path:"/"});
    setRedirectToLogin(false);
  };

  useEffect(() => {
    if (process.env.REACT_APP_ENV === "dev") {
      getToken(process.env.REACT_APP_EMAIL, process.env.REACT_APP_PASSWORD)
        .then((response) => {
          if (
            response.id &&
            response.name &&
            response.email &&
            response.token &&
            response.accounts
          ) {
            setUserID(response.id);
            setUsername(response.name);
            setUserEmail(response.email);
            response.accounts.sort();
            setUserAccountID(response.accounts[0]);
            setToken(response.token);
            setSuperUser(response.superuser ? true : false);
            getAccounts(response.token)
              .then((resp) => {
                if (resp.accounts) {
                  setUserAccount(
                    resp.accounts.find((a) => a.id === response.accounts[0])
                  );
                  setUserAccounts(resp.accounts);
                } else {
                  throw new Error("accounts not found");
                }
              })
              .catch((error) => {
                console.log(error);
              });
            setAuth(true);
          } else {
            throw new Error("invalid response from API");
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      if (cookies.user_id && cookies.token) {
        getUsers(cookies.token)
          .then((response) => {
            if (response.status === 401) {
              handleRedirectToLogin();
            }
            if (response.users) {
              const user = response.users.find(
                (user) => user.id === Number(cookies.user_id)
              );
              if (user) {
                setUserEmail(user.email);
                setSuperUser(user.superuser ? true : false);
                setToken(cookies.token);
                setUserID(user.id);
                setUsername(user.name);
                setUserAccountID(user.accounts[0]);
                getAccounts(cookies.token)
                  .then((resp) => {
                    if (resp.accounts) {
                      setUserAccount(
                        resp.accounts.find((a) => a.id === user.accounts[0])
                      );
                      setUserAccounts(resp.accounts);
                    } else {
                      throw new Error("accounts not found");
                    }
                  })
                  .catch((error) => {
                    console.log(error);
                  });
                setAuth(true);
              } else {
                console.log("user not found");
                handleRedirectToLogin();
              }
            } else {
              console.log("users not found");
              handleRedirectToLogin();
            }
          })
          .catch((error) => {
            console.log("error", error);
            handleRedirectToLogin();
          });
      } else {
        handleRedirectToLogin();
      }
    } 
  }, [cookies.token, cookies.user_id]);

  useEffect(() => {
    if (userAccounts.length !== 0) {
      setUserAccount(userAccounts.find((a) => a.id === userAccountID));
    }
  }, [userAccountID, userAccounts]);

  useEffect(() => {
    if (userAccount.owners?.includes(userID)) {
      setUserAccountRole("OWNER")
    } else if (userAccount.admins?.includes(userID)) {
      setUserAccountRole("ADMIN")
    } else {
      setUserAccountRole("USER")
    };
  }, [userID, userAccount]);

  const createSuccess = (message) => {
    setSnackbarSeverity("success");
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const createError = (message) => {
    setSnackbarSeverity("error");
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };



  const muiTheme = useTheme();
  const belowMdScreen = useMediaQuery(muiTheme.breakpoints.down("md"));

  if (belowMdScreen && process.env.REACT_APP_ENV !== "dev") {
    return (
      <ThemeProvider theme={theme}>
        <ScreenTooSmall />
      </ThemeProvider>
    );
  };

  if (redirectToLogin) {
    return (
      <ThemeProvider theme={theme}>
        <SnackbarContext.Provider
          value={{
            createSuccess,
            createError,
          }}
        >
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbarSeverity}
              variant="filled"
              sx={{ width: "100%" }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
          <Login setCookies={setCookies}/>
        </SnackbarContext.Provider>
      </ThemeProvider>
    );
  };
  
  return (
    <ThemeProvider theme={theme}>
      <SnackbarContext.Provider
        value={{
          createSuccess,
          createError,
        }}
      >
        <AuthContext.Provider
          value={{
            auth,
            superUser,
            userID,
            username,
            userEmail,
            userAccount,
            userAccounts,
            userAccountID,
            setUserAccountID,
            userAccountRole,
            token
          }}
        >
          {auth ? (
            <BrowserRouter>
              <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              >
                <Alert
                  onClose={handleCloseSnackbar}
                  severity={snackbarSeverity}
                  variant="filled"
                  sx={{ width: "100%" }}
                >
                  {snackbarMessage}
                </Alert>
              </Snackbar>
              <Routes>
                <Route
                  exact
                  path="/"
                  element={
                    <ProtectedRoute auth={auth}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  exact
                  path="/model"
                  element={
                    <ProtectedRoute auth={auth}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/model/:id"
                  element={
                    <ProtectedRoute auth={auth}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/model/:id/edit"
                  element={
                    <ProtectedRoute auth={auth}>
                      <Dashboard page="edit" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <ProtectedRoute auth={auth} userID={userID} userAccount={userAccount} requiredRole={"ADMIN"}>
                      <Account />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account/:id/"
                  element={
                    <ProtectedRoute auth={auth} userID={userID} userAccount={userAccount} requiredRole={"ADMIN"}>
                      <Account />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account/:id/:service"
                  element={
                    <ProtectedRoute auth={auth} userID={userID} userAccount={userAccount} requiredRole={"ADMIN"}>
                      <Account />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account/:id/:service/:integration"
                  element={
                    <ProtectedRoute auth={auth} userID={userID} userAccount={userAccount} requiredRole={"ADMIN"}>
                      <IntegrationManagement />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </BrowserRouter>
          ) : (
            <Box sx={{ width: "100%" }}>
              <LinearProgress />
            </Box>
          )}
        </AuthContext.Provider>
      </SnackbarContext.Provider>
    </ThemeProvider>
  );
}

const ProtectedRoute = ({ auth, userID, userAccount, requiredRole, children }) => {
  if (auth) {
    if (requiredRole && userAccount.owners && userAccount.admins) {
      if (requiredRole === "OWNER") {
        if (userAccount.owners.includes(userID)) {return children}
        else {return <Navigate to="/" />}
      }
      if (requiredRole === "ADMIN") {
        if (userAccount.owners.includes(userID) || userAccount.admins.includes(userID)) {return children}
        else {return <Navigate to="/" />}
      } else {
        return children
      }
    } else {
      return children
    }
  } else {
    return <Navigate to="/" />
  }
};

export default App;
